import { Api } from "@penn/web-common";
import { normalizeMaxThreadSizes } from "@/utils/max-thread-size-options";
import { unwrap } from "@penn/web-common";

export interface FastenerBrand {
  code: string;
  displayName: string;
  displayOrder: number;
}

export async function getFastenerBrands(): Promise<FastenerBrand[]> {
  const res = await Api.get("/v1/filter-options/fastener-brands");
  const payload = unwrap<unknown>(res.data);
  if (!Array.isArray(payload)) {
    console.warn("[getFastenerBrands] unexpected payload", res.data);
    return [];
  }
  return payload as FastenerBrand[];
}

export async function getInstallationMaterials(): Promise<string[]> {
  const res = await Api.get("/v1/filter-options/installation-materials");
  const payload = unwrap<unknown>(res.data);
  if (!Array.isArray(payload)) {
    console.warn("[getInstallationMaterials] unexpected payload", res.data);
    return [];
  }
  return payload.filter((m): m is string => typeof m === "string");
}

export async function getMaxThreadSizes(
  fastenerBrand?: string,
): Promise<number[]> {
  const res = await Api.get("/v1/filter-options/max-thread-sizes", {
    params: {
      ...(fastenerBrand ? { fastenerBrand } : {}),
    },
  });
  const payload = unwrap<unknown>(res.data);
  if (!Array.isArray(payload)) {
    console.warn("[getMaxThreadSizes] unexpected payload", res.data);
    return [];
  }
  return normalizeMaxThreadSizes(payload);
}

export type RegionCode = "EU" | "NA" | "ASIA";

export interface SelectorEvalRequest {
  region: RegionCode;
  fastenerBrand?: string | string[];
  maxThreadSize?: number;
  requireProcessMonitoring?: boolean;
  requireRobotIntegration?: boolean;
  minForceKn?: number;
  maxForceKn?: number;
  applicationIds?: string[];
}

export interface SelectorProduct {
  productId: string;
  name: string;
  category: string;
  subcategory: string;
  responsibility: string | null;
  imageLink: string;
  link: string;
  supportsPemClinch: boolean;
  supportsBlindRivet: boolean;
  supportsProfile: boolean;
  supportsOther: boolean;
  featuresProcessMonitoring: boolean;
  featuresRobotIntegration: boolean;
  maxSize: number | null;
  maxSizeLabel: string;
  maxForceKn: number | null;
  availability: { eu: boolean; na: boolean; asia: boolean };
  availableInRegion: boolean;
  costModelId: string | null;
  costModelAvailable: boolean;
  keyCharacteristics: string[];
}

export interface RequiredForce {
  forceLbs: number;
  forceKn: number;
}

export interface SelectorEvalResponse {
  products: SelectorProduct[];
  remainingValidFilters: {
    fastenerBrand: string[];
    maxThreadSize: number[];
  };
  requiredForce: RequiredForce | null;
}

export async function evalSelector(
  body: SelectorEvalRequest,
): Promise<SelectorEvalResponse> {
  const res = await Api.post("/v1/selector/eval", body);
  return unwrap<SelectorEvalResponse>(res.data);
}
